import {
  DynamicRibbonFormat,
  HomeTileConfigStatus,
  HomeTileType,
  IPersonaRuleNode,
  IRibbonColors,
  IRibbonStyle,
  PersonaRuleOperator,
  RibbonPosition,
} from "@/api_framework/api_modals/HomeTileConfig";

export type PersonaLogicMode = "BASIC" | "ADVANCED";

export interface IBasicPersonaLogic {
  include: string[];
  include_operator: PersonaRuleOperator;
  is_exclude_enabled: boolean;
  exclude: string[];
  exclude_operator: PersonaRuleOperator;
}

export const DEFAULT_BASIC_PERSONA_LOGIC: IBasicPersonaLogic = {
  include: [],
  include_operator: "AND",
  is_exclude_enabled: false,
  exclude: [],
  exclude_operator: "AND",
};

export interface IPersonaLogicFormFields {
  persona_logic_mode: PersonaLogicMode;
  basic_persona: IBasicPersonaLogic;
  persona_combination: string;
}

export interface IPersonaCombinationSummary {
  id: string;
  persona_rule: IPersonaRuleNode | null;
  text: string;
  ribbon?: IRibbonStyle;
  status: HomeTileConfigStatus;
}

export interface IHomeTileConfigFormFields extends IPersonaLogicFormFields {
  tile_id: string;
  ribbon_text: string;
  ribbon: IRibbonStyle;
  status: HomeTileConfigStatus;
  overrides_dynamic: boolean;
}

export const PERSONA_LOGIC_MODES: { key: PersonaLogicMode; label: string }[] = [
  { key: "BASIC", label: "Basic" },
  { key: "ADVANCED", label: "Advanced" },
];

export const PERSONA_RULE_OPERATORS: { key: PersonaRuleOperator; label: string }[] = [
  { key: "AND", label: "AND" },
  { key: "OR", label: "OR" },
];

export const toSearchKey = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\p{Extended_Pictographic}]+/gu, " ")
    .trim();

export const matchesSearch = (query: string, values: (string | null | undefined)[]): boolean => {
  const words = toSearchKey(query).split(" ").filter(Boolean);
  if (!words.length) return true;
  return values.some((value) => {
    if (!value) return false;
    const key = toSearchKey(value);
    return words.every((word) => key.includes(word));
  });
};

export const TILE_TYPE_LABEL: Record<HomeTileType, string> = {
  QCOMM: "QComm",
  SERVICE: "Service",
};

export const getTileVisibilityTitle = (isVisible: boolean): string =>
  isVisible ? "Showing on the app home screen" : "Hidden on the app home screen";

export const PERSONA_OPERATORS = ["AND", "OR", "NOT", "(", ")"] as const;

export const RIBBON_POSITION_LABEL: Record<RibbonPosition, string> = {
  top_left: "Top Left",
  top_center: "Top Center",
  top_right: "Top Right",
  bottom_left: "Bottom Left",
  bottom_center: "Bottom Center",
  bottom_right: "Bottom Right",
  top_full_width: "Top Full Width",
  full_width: "Bottom Full Width",
};

export const RIBBON_POSITION_CELLS: RibbonPosition[] = [
  "top_left",
  "top_center",
  "top_right",
  "bottom_left",
  "bottom_center",
  "bottom_right",
];

export const RIBBON_POSITION_CLASSNAME: Record<RibbonPosition, string> = {
  top_left: "-top-3 left-1",
  top_center: "-top-3 left-1/2 -translate-x-1/2",
  top_right: "-top-3 right-1",
  bottom_left: "-bottom-3 left-1",
  bottom_center: "-bottom-3 left-1/2 -translate-x-1/2",
  bottom_right: "-bottom-3 right-1",
  top_full_width: "-top-3 left-0 right-0",
  full_width: "-bottom-3 left-0 right-0",
};

export const isFullWidthRibbon = (position: RibbonPosition): boolean =>
  position === "top_full_width" || position === "full_width";

export const RIBBON_TEXT_MAX_LENGTH = 16;

export const RIBBON_TEXT_MIN_LENGTH = 2;

export const RIBBON_TEXT_CONTENT_PATTERN = /[\p{L}\p{N}\p{Extended_Pictographic}]/u;

const EMOJI_MODIFIER_PATTERN = /\uFE0E|\uFE0F|\p{Emoji_Modifier}|\u200D\p{Extended_Pictographic}/gu;

export const getRibbonTextLength = (value: string): number =>
  Array.from(value.replace(EMOJI_MODIFIER_PATTERN, "")).length;

export const DYNAMIC_RIBBON_EXAMPLES: { format: DynamicRibbonFormat; text: string; label: string }[] = [
  { format: "discount", text: "20% OFF", label: "Discount" },
  { format: "usage_fraction", text: "4/5 left", label: "Benefits utilized" },
  { format: "usage_remaining", text: "1 left", label: "Benefits left" },
];

export const DEFAULT_DYNAMIC_RIBBON_FORMAT: DynamicRibbonFormat = "usage_fraction";

export type RibbonTextSuggestion = IRibbonColors & { text: string };

export const RIBBON_TEXT_SUGGESTIONS: RibbonTextSuggestion[] = [
  { text: "20% Off", bg_color: "#2B59D3", text_color: "#FFFFFF", border_color: "#DEDEDE" },
  { text: "12% Off", bg_color: "#FFFFFF", text_color: "#00861E", border_color: "#DEDEDE" },
  { text: "New", bg_color: "#F24661", text_color: "#FFFFFF", border_color: "#DEDEDE" },
  { text: "# Instant 30 min", bg_color: "#077E21", text_color: "#FFFFFF", border_color: "#DEDEDE" },
];

export const cardClassName = "rounded-xl border border-slate-200 bg-white shadow-sm";

export const fieldLabelClassName = "mb-1.5 flex items-center gap-1 text-xs font-medium text-slate-600";

export const textInputClassName =
  "h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 shadow-sm focus:border-[#193A6A] focus:outline-none";

export const sectionLabelClassName = "text-[10px] font-semibold uppercase tracking-wide text-slate-400";

export const tileTypeChipClassName =
  "rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500";

export const secondaryButtonClassName =
  "rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 shadow-sm transition-colors hover:bg-slate-50";

export const primaryButtonClassName =
  "flex items-center gap-1.5 rounded-lg bg-[#193A6A] px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#132c52]";

export type StatusFilter = HomeTileConfigStatus | "ALL";

export const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "ALL", label: "All" },
  { key: "ACTIVE", label: "Active" },
  { key: "INACTIVE", label: "InActive" },
];

export const STATUS_COLUMN_FILTER_OPTIONS = STATUS_FILTERS.map(({ key, label }) => ({ value: key, label }));

export type RibbonTypeFilter = "ALL" | "STATIC" | "DYNAMIC";

export const RIBBON_TYPE_FILTERS: { key: RibbonTypeFilter; label: string }[] = [
  { key: "ALL", label: "All" },
  { key: "STATIC", label: "Only Static" },
  { key: "DYNAMIC", label: "Dynamic" },
];
